import React, { useEffect } from "react";
import "./styles.css";
import { Input } from "../../components/common/Input";
import { Button } from "../../components/common/Button";
import { PageNavigation } from "../../components/common/PageNavigation";
import { useForm, useWatch } from "react-hook-form";
import { REGEXES } from "../../constants/common";
import { RootState } from "../../store";
import { UserActionCreator } from "../../store/userActions";
import { connect } from "react-redux";
import {
    selectUserError,
    selectUserErrorMessage,
    selectUserPending,
    selectUserReducer,
} from "../../store/selectors";
import { useCookies } from "react-cookie";
import { Link } from "react-router-dom";
import {
    ANALYTICS_EVENTS,
    trackAnalyticsEvent,
} from "../../analytics";

export const _SignUp = ({
                            signUpRequest,
                            pending,
                            error,
                            errorMessage,
                            setNotification,
                            userReducer,
                        }) => {
    const [, setCookie] = useCookies(["userToken"]);

    const {
        register,
        handleSubmit,
        setError,
        control,
        formState: { errors },
    } = useForm({ mode: "onChange" });

    const password = useWatch({ control, name: "password" });

    const onSubmit = async (data) => {
        const action = await signUpRequest(data);
        const newToken = action?.payload?.data?.data?.token;

        if (!newToken) return;

        setCookie("userToken", newToken, { path: "/" });
        trackAnalyticsEvent(ANALYTICS_EVENTS.AUTH_SIGN_UP_SUCCEEDED);
        setNotification({
            isOpen: true,
            value: {
                title: "Отлично!",
                description: `Вы успешно зарегистрировались. На ваш e-mail отправлено письмо с ссылкой для подтверждения вашей электронной почты. Обязательно подтвердите вашу электронную почту, это нужно для восстановления доступа к аккаунту.`,
                closeIcon: true,
            },
        });
    };

    // ✅ отображаем ошибки из userReducer
    useEffect(() => {
        if (!error) return;

        // Полевые ошибки (phone, email и т.п.)
        if (userReducer?.fieldErrors && Object.keys(userReducer.fieldErrors).length > 0) {
            Object.entries(userReducer.fieldErrors).forEach(([field, message]) => {
                setError(field, { message: String(message) });
            });
        }

        // Общая ошибка (если нет fieldErrors)
        else if (userReducer?.errorMessage) {
            setError("email", { message: userReducer.errorMessage });
        }
    }, [error, userReducer?.fieldErrors, userReducer?.errorMessage, setError]);

    return (
      <>
        <div className="mx-auto w-full max-w-[800px] px-5 pt-4">
          <PageNavigation backLabel="К авторизации" backTo="/signin" showBackIcon={false} />
        </div>
        <div className="sign-up_container">
            <h2>Регистрация</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="sign-up_form-container">
                <div className="sign-up_input-item">
                    <Input
                        register={register("email", {
                            required: { message: "введите e-mail", value: true },
                            pattern: {
                                message: "некорректный e-mail",
                                value: REGEXES.email,
                            },
                        })}
                        name="email"
                        type="email"
                        placeholder="e-mail"
                        label="введите e-mail"
                        error={!!errors.email}
                        errorMessage={errors.email?.message}
                    />
                </div>

                <div className="sign-up_input-item">
                    <Input
                        register={register("name", {
                            required: { message: "введите имя", value: true },
                        })}
                        name="name"
                        type="text"
                        placeholder="имя"
                        label="введите ваше имя"
                        error={!!errors.name}
                        errorMessage={errors.name?.message}
                    />
                </div>

                <div className="sign-up_input-item">
                    <Input
                        register={register("phone", {
                            required: { message: "введите номер телефона", value: true },
                            pattern: {
                                message: "некорректный номер телефона",
                                value: REGEXES.phone,
                            },
                        })}
                        name="phone"
                        placeholder="номер телефона"
                        label="введите действующий номер телефона"
                        error={!!errors.phone}
                        errorMessage={errors.phone?.message}
                    />
                </div>

                <div className="sign-up_input-item">
                    <Input
                        register={register("whatsapp")}
                        name="whatsapp"
                        placeholder="WhatsApp"
                        label="WhatsApp (необязательно)"
                    />
                </div>

                <div className="sign-up_input-item">
                    <Input
                        register={register("telegram")}
                        name="telegram"
                        placeholder="Telegram"
                        label="Telegram (необязательно)"
                    />
                </div>

                <div className="sign-up_input-item">
                    <Input
                        register={register("instagram")}
                        name="instagram"
                        placeholder="Instagram"
                        label="Instagram (необязательно)"
                    />
                </div>

                <div className="sign-up_input-item">
                    <Input
                        register={register("password", {
                            required: { message: "введите пароль", value: true },
                            pattern: {
                                message: "некорректный пароль",
                                value: REGEXES.password,
                            },
                        })}
                        name="password"
                        type="password"
                        placeholder="пароль"
                        label="введите пароль"
                        error={!!errors.password}
                        errorMessage={errors.password?.message}
                    />
                </div>

                <div className="sign-up_input-item">
                    <Input
                        register={register("checkPassword", {
                            required: { message: "повторите пароль", value: true },
                            validate: (value) =>
                                value === password || "пароли не совпадают",
                        })}
                        name="checkPassword"
                        type="password"
                        placeholder="повторите пароль"
                        label="повторите пароль"
                        error={!!errors.checkPassword}
                        errorMessage={errors.checkPassword?.message}
                    />
                </div>

                <div className="sign-up_input-item">
                    <Button pending={pending} text="регистрация" />
                </div>
            </form>

            <div className="sign-up_reg-advice">
                Уже есть аккаунт?{" "}
                <Link className="sign-in_link" to="/signin/">
                    Авторизация
                </Link>
            </div>
        </div>
      </>
    );
};

// ──────────────────────────────── Redux
const mapStateToProps = (state: RootState) => ({
    pending: selectUserPending(state),
    error: selectUserError(state),
    errorMessage: selectUserErrorMessage(state),
    userReducer: selectUserReducer(state),
});

const mapDispatchToProps = {
    signUpRequest: UserActionCreator.userSignUp,
    setNotification: UserActionCreator.setNotification,
};

export const SignUp = connect(mapStateToProps, mapDispatchToProps)(_SignUp);
